import {
  Resources,
  TemplateAdapter,
  TransactionAdapter,
  TransactionStatus,
  TransactionStatusType,
  WorkflowStatus,
  WorkflowSubmitDatasetTaskMetadata,
  WorkflowTaskAdapter,
  WorkflowTaskStatus,
  WorkflowTaskType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import styles from "./WorkflowTaskSubmitDataset.module.scss";
import {
  Banner,
  Button,
  FileInput,
  Form,
  KeyValueList,
  NumberDataDisplay,
  TextDataDisplay,
  useForm,
  useToast,
} from "@validereinc/common-components";
import React, { useMemo, useState } from "react";
import mime from "mime";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import { snakeCase } from "es-toolkit/compat";
import { downloadBlob } from "@validereinc/utilities";
import { ExceptionUtils } from "#src/utils/exception";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { DataSetSubmissionDetailRoutePath } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail";
import FileService from "#src/components/Services/FileService";

type WorkflowSubmitDatasetProps = {
  workflowTask: WorkflowTaskType;
  datasetId?: string;
};

const acceptedMimeTypes = [
  mime.getType("csv"),
  mime.getType("xls"),
  mime.getType("xlsx"),
  mime.getType("json"),
].join(",");

const SubmitDatasetTaskFormSchema = z.object({
  dataset: z.string().uuid(),
  files: z
    .array(z.instanceof(File))
    .refine((files) => files.every((f) => acceptedMimeTypes.includes(f.type)), {
      message: "Invalid file type",
    }),
});
type SubmitDatasetTaskFormType = z.infer<typeof SubmitDatasetTaskFormSchema>;

const getBannerDetails = (
  transactionId?: string,
  transactionStatus?: TransactionStatusType,
  uploadError?: boolean
): {
  titleText: string;
  description: string;
  variant: "success" | "error" | "warning" | "loading";
  action: React.ReactElement | null;
} | null => {
  if (transactionStatus === TransactionStatus.COMPLETED) {
    return {
      titleText: "Successfully uploaded data",
      description: "",
      variant: "success",
      action: null,
    };
  } else if (transactionStatus === TransactionStatus.FAILED) {
    return {
      titleText: "Failed to upload data",
      description: "Data submission failed in validation.",
      variant: "error",
      action: (
        <>
          <RoutingLink
            to={DataSetSubmissionDetailRoutePath.toLink({
              pathParams: {
                transactionId: transactionId ?? "",
              },
            })}
          >
            View Details
          </RoutingLink>
        </>
      ),
    };
  } else if (uploadError) {
    return {
      titleText: "Failed to upload data",
      description: "Data submission failed in ingestion.",
      variant: "error",
      action: null,
    };
  } else if (transactionStatus === TransactionStatus.PENDING) {
    return {
      titleText: "Uploading Data",
      description: "You may view the dataset details once completed",
      variant: "loading",
      action: null,
    };
  } else if (
    transactionStatus === TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS
  ) {
    return {
      titleText: "Not all expected rows have been filled.",
      description: "Upload additional data or manually fill in records",
      variant: "warning",
      action: null,
    };
  } else {
    return null;
  }
};

const cx = classNames.bind(styles);
export const WorkflowTaskSubmitDataset = ({
  datasetId,
  workflowTask,
}: WorkflowSubmitDatasetProps) => {
  const { metadata } = workflowTask;
  //assume only one submission at a time for now
  const { dataset_submission_ids } =
    metadata as WorkflowSubmitDatasetTaskMetadata;
  const currentSubmissionId =
    dataset_submission_ids?.length > 0 ? dataset_submission_ids[0] : "";
  const { claims } = useAuthenticatedContext();

  const queryClient = useQueryClient();
  const [uploadError, setUploadError] = useState<boolean>(false);
  const { toast } = useToast();

  const { data: datasets, isLoading: areDatasetsLoading } = useListDatasets();
  const { data: templateBlob, isLoading: isTemplateBlobLoading } = useQuery(
    ["datasets", "template", datasetId ?? ""],
    () =>
      TemplateAdapter.dataset.getOne({
        datasetId: datasetId ?? "",
        companyId: claims.company.id!,
      }),
    {
      enabled: Boolean(datasetId) && Boolean(claims?.company?.id),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: transactionData, isLoading: isTransactionLoading } = useQuery(
    [Resources.TRANSACTION, currentSubmissionId],
    () =>
      TransactionAdapter.getOne({
        id: currentSubmissionId,
        meta: { history: false },
      }),
    {
      enabled: Boolean(currentSubmissionId),
      refetchInterval:
        workflowTask.status !== WorkflowStatus.COMPLETE ? 10000 : undefined,
    }
  );
  const transaction = transactionData?.data[0];
  const selectedDataset = useMemo(
    () => datasets?.data.find((d) => d.id === datasetId),
    [datasets]
  );

  const form = useForm<SubmitDatasetTaskFormType>();
  const files = form.watch("files");

  const handleDownloadTemplateClick = () => {
    if (!templateBlob) {
      toast.push({
        intent: "error",
        description: "Template not available!",
      });
      return;
    }

    const fileName =
      `${selectedDataset?.name ? `${snakeCase(selectedDataset?.name)}_` : ""}import_template`.toLowerCase();

    try {
      downloadBlob(fileName, templateBlob);
      toast.push({
        intent: "success",
        description: `Template ${fileName}.xlsx downloaded.`,
      });
    } catch (err) {
      toast.push({
        intent: "error",
        description: `Template failed to download.`,
      });
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to download import template for templated configuration",
      });
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!datasetId || !claims) {
      return {
        file,
        uploadedFile: null,
        transactionId: null,
      };
    }

    // extract key file details
    const { name, type: contentType, lastModified } = file;

    // create a file in the file upload service
    const {
      status: createFileStatus,
      data: {
        data: [uploadedFileData],
        meta: { transaction_id: transactionId },
      },
    } = await FileService.createFile({
      name,
      contentType,
      description:
        "A user-uploaded file through the import data action component",
      tags: {
        lastModified: new Date(lastModified).toISOString(),
      },
      datasetId: datasetId,
      companyId: claims.company?.id,
    })!;

    if (createFileStatus !== 200 && createFileStatus !== 201) {
      throw new Error("Could not create an entry in the file upload service");
    }

    // extract the ID and upload URL of the file entry in the file upload service
    const { file_id: fileId, uri: uploadUrl } = uploadedFileData;

    // upload the file to the pre-signed upload URL
    const { status: uploadFileStatus } = await FileService.uploadFile({
      url: uploadUrl,
      fileBlob: file,
    })!;

    if (uploadFileStatus !== 200 && uploadFileStatus !== 201) {
      throw new Error(
        "Could not upload file to the created entry in file upload service"
      );
    }

    // poll and check to see if the file scanning in the file upload service has completed
    let scanStatus = "unknown";
    let pollLimit = 10;

    while (pollLimit > 0 && !["safe", "unsafe"].includes(scanStatus)) {
      const {
        data: {
          data: [{ file_scan_status }],
        },
      } = await FileService.getFileDownloadUrl(fileId)!;

      scanStatus = file_scan_status;

      // scan status re-check polling interval
      await new Promise((res) => setTimeout(res, 1000));
      pollLimit -= 1;
    }

    // verify based on the scan result

    if (scanStatus !== "safe") {
      throw new Error(
        "The uploaded file has been deemed as unsafe or failed to complete verification"
      );
    }

    return {
      file,
      uploadedFile: {
        ref: fileId,
        name,
      },
      transactionId,
    };
  };

  const datasetSubmissionMutation = useMutation({
    mutationFn: async (formValues: SubmitDatasetTaskFormType) => {
      await SubmitDatasetTaskFormSchema.safeParseAsync(formValues);
      const file = formValues.files.pop();
      if (!file) {
        throw new Error("No file to upload");
      }

      const { transactionId } = await handleFileUpload(file);

      if (!transactionId) {
        throw new Error(
          "Transaction ID not returned for initiated data import"
        );
      }

      const transaction = await TransactionAdapter.getOne({
        id: transactionId,
        meta: { history: false },
      });

      if (!transaction?.data.length) {
        throw new Error("No transactions found for initiated transaction");
      }

      await WorkflowTaskAdapter.updateOne({
        id: workflowTask.id,
        data: {
          dataset_submission_ids: [transactionId],
        },
        previousData: workflowTask,
      });
    },
    onSuccess: async () => {
      toast.push({
        intent: "success",
        description: `Data import started successfully.`,
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.TRANSACTION],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          Resources.WORKFLOW,
          "task",
          "getOne",
          { id: workflowTask.id },
        ],
      });
      form.reset();
    },
    onError: (err: Error) => {
      console.error(err);
      setUploadError(true);
    },
  });

  const clearForm = () => {
    //necessary for re-selecting a cleared file
    const inputRef = document.querySelector<HTMLInputElement>(
      'input[name="files"]'
    );
    if (inputRef) {
      inputRef.value = "";
    }

    form.reset();
  };
  const clearSubmissionMutation = useMutation({
    mutationFn: async () =>
      await WorkflowTaskAdapter.updateOne({
        id: workflowTask.id,
        data: {
          dataset_submission_ids: [],
        },
        previousData: workflowTask,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.TRANSACTION],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          Resources.WORKFLOW,
          "task",
          "getOne",
          { id: workflowTask.id },
        ],
      });
      clearForm();
    },
    onError: (err: Error) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: `Failed to clear data`,
      });
    },
  });

  const bannerDetails = getBannerDetails(
    currentSubmissionId,
    transaction?.status,
    uploadError
  );

  return (
    <Form
      {...form}
      onSubmit={() => {
        form.handleSubmit((formData) => {
          setUploadError(false);
          datasetSubmissionMutation.mutate(formData);
        })();
      }}
    >
      {bannerDetails && workflowTask.status !== WorkflowTaskStatus.COMPLETE && (
        <div className={cx("banner-container")}>
          <Banner
            actionContent={bannerDetails.action ?? undefined}
            description={bannerDetails.description}
            isDismissable={false}
            titleText={bannerDetails.titleText}
            variant={bannerDetails.variant}
          />
        </div>
      )}
      <div className={cx("container")}>
        <div className={cx("title-container")}>
          <p className={cx("title")}>Data Submission</p>
          {!transaction && (
            <Button
              icon="download-simple"
              variant="outline"
              onClick={handleDownloadTemplateClick}
              isLoading={Boolean(
                areDatasetsLoading || (datasetId && isTemplateBlobLoading)
              )}
              disabled={!datasetId}
            >
              Download Template
            </Button>
          )}
          {!!transaction && (
            <div className={cx("button-container")}>
              {workflowTask.status !== WorkflowTaskStatus.COMPLETE && (
                <Button
                  variant="outline"
                  onClick={() => clearSubmissionMutation.mutate()}
                  isLoading={
                    Boolean(isTransactionLoading) ||
                    clearSubmissionMutation.isLoading
                  }
                  style={{ minHeight: 38 }}
                >
                  Re-Submit Data
                </Button>
              )}
              <RoutingLink
                to={DataSetSubmissionDetailRoutePath.toLink({
                  pathParams: {
                    transactionId: transaction.transaction_id ?? "",
                  },
                })}
              >
                <Button
                  icon="share"
                  variant="outline"
                  isLoading={Boolean(isTransactionLoading)}
                >
                  View
                </Button>
              </RoutingLink>
            </div>
          )}
        </div>
        {!transaction && (
          <>
            <FileInput
              name="files"
              placeholder="Drag .csv or .exe files here (max. 20 MB)"
              isRequired={true}
              rules={{
                required: "A file with data to import is required",
              }}
              acceptedFileTypes={acceptedMimeTypes}
              isDisabled={!datasetId || form.formState.isSubmitting}
              allowMultiple={false}
              buttonProps={{
                variant: "primary",
              }}
              className={cx("input")}
              isLoading={areDatasetsLoading}
            />
            {files?.length > 0 && (
              <div className={cx("button-container")}>
                <Button
                  key="clear"
                  onClick={() => clearForm()}
                >
                  Clear
                </Button>
                <Button
                  key="submit"
                  variant="primary"
                  type="submit"
                  isLoading={datasetSubmissionMutation.isLoading}
                >
                  Submit Data
                </Button>
              </div>
            )}
          </>
        )}
        {!!transaction && (
          <KeyValueList
            isLoading={areDatasetsLoading}
            variant="shaded"
            className={cx("key-value-list")}
            data={[
              {
                title: "Dataset Name",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={selectedDataset?.name ?? "-"}
                  />
                ),
              },
              {
                title: "Import Type",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={"Data Upload"}
                  />
                ),
              },
              {
                title: "File Name",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={transaction?.original_file_name ?? "-"}
                  />
                ),
              },
              {
                title: "Total Rows",
                value: (
                  <NumberDataDisplay value={transaction?.total_count ?? 0} />
                ),
              },
              {
                title: "Successful Rows",
                value: (
                  <NumberDataDisplay
                    value={transaction?.successful_count ?? 0}
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </Form>
  );
};
